import { css, html, LitElement, nothing, type PropertyValues } from "lit";

import { controlStyles } from "../styles/tokens";

/**
 * <modulina-lock-panel> — the confirm step of take-for-work
 * (spec-artifact-locking §8), in the two shapes the spec names:
 *
 *   "take"     — „Paimti darbui" with the OPTIONAL note („ką darysi"),
 *                which colleagues then see on the file's badge.
 *   "takeover" — „Perimti…" behind a CONFIRM that names whose lock it is
 *                and how old (parent-composed text: it owns the clock),
 *                plus the same note field, because after a takeover the
 *                file is the user's and the badge should say why.
 *
 * Props in / events out like every other child (`lock-confirm` {note},
 * `lock-cancel`): it never sees the transport — the parent runs the
 * lock/unlock calls and the download that follows a take.
 *
 * A takeover interrupts a colleague's work, so the confirm button is NOT
 * the primary/default action and the panel does not submit on Enter in the
 * note field for that mode — the user presses the named button.
 */

export type LockPanelMode = "take" | "takeover";

export class ModulinaLockPanel extends LitElement {
  static override properties = {
    mode: { attribute: false },
    fileName: { attribute: false },
    confirmText: { attribute: false },
    busy: { attribute: false },
    note: { state: true },
  };

  /** null = idle (the parent renders nothing). */
  declare mode: LockPanelMode | null;
  declare fileName: string;
  /** For "takeover": the parent-composed „kieno ir kada" sentence
   * (ui/src/lib/locking.ts takeoverConfirmText — the clock lives there,
   * not here, so this component stays pure props-in). */
  declare confirmText: string;
  declare busy: boolean;
  /** Local draft of the optional note. */
  declare note: string;

  constructor() {
    super();
    this.mode = null;
    this.fileName = "";
    this.confirmText = "";
    this.busy = false;
    this.note = "";
  }

  static override styles = [
    controlStyles,
    css`
      :host {
        display: block;
      }
      .panel {
        border: 1px solid var(--m-color-border);
        border-radius: var(--m-radius);
        background: var(--m-color-surface);
        box-shadow: var(--m-shadow);
        padding: var(--m-space-3);
        display: flex;
        flex-direction: column;
        gap: var(--m-space-2);
      }
      .file-line {
        font-size: var(--m-fs-small);
      }
      .confirm {
        font-size: var(--m-fs-small);
        background: var(--m-color-danger-soft);
        color: var(--m-color-danger);
        border-radius: var(--m-radius-small);
        padding: var(--m-space-2) var(--m-space-3);
      }
      .buttons {
        display: flex;
        gap: var(--m-space-2);
      }
    `,
  ];

  protected override willUpdate(changed: PropertyValues<this>): void {
    // Fresh note draft every time the panel opens or switches target, so a
    // previous take's reason never rides along to another file.
    if (changed.has("mode") || changed.has("fileName")) this.note = "";
  }

  protected override updated(changed: PropertyValues<this>): void {
    // Keyboard flow: opening the panel puts the caret in the note field —
    // the note is the only thing to type, and Esc/„Atšaukti" backs out.
    if (changed.has("mode") && this.mode !== null) {
      this.renderRoot.querySelector<HTMLElement>("#lock-note")?.focus();
    }
  }

  override render() {
    if (this.mode === null) return nothing;
    const takeover = this.mode === "takeover";
    return html`
      <div class="panel" role="group" aria-label=${takeover ? "Perimti failą" : "Paimti darbui"}>
        <span class="file-line">Failas: ${this.fileName}</span>
        ${takeover ? html`<p class="confirm" role="alert">${this.confirmText}</p>` : nothing}
        <div>
          <label for="lock-note">Ką darysi? (nebūtina)</label>
          <input
            id="lock-note"
            type="text"
            maxlength="200"
            .value=${this.note}
            ?disabled=${this.busy}
            @input=${(e: Event) => (this.note = (e.target as HTMLInputElement).value)}
            @keydown=${this.onNoteKeydown}
          />
        </div>
        <div class="buttons">
          <button
            class=${takeover ? "" : "primary"}
            ?disabled=${this.busy}
            @click=${this.emitConfirm}
          >
            ${takeover ? "Perimti" : "Paimti darbui"}
          </button>
          <button ?disabled=${this.busy} @click=${this.emitCancel}>Atšaukti</button>
        </div>
      </div>
    `;
  }

  /** Enter confirms a plain take (the note is one short line); a TAKEOVER
   * never submits on Enter — interrupting a colleague is a deliberate
   * button press. Esc backs out of both. */
  private onNoteKeydown = (e: KeyboardEvent): void => {
    if (e.key === "Enter" && this.mode === "take") {
      e.preventDefault();
      this.emitConfirm();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      this.emitCancel();
    }
  };

  private emitConfirm = (): void => {
    if (this.busy) return;
    this.dispatchEvent(
      new CustomEvent("lock-confirm", {
        detail: { note: this.note.trim() },
        bubbles: true,
        composed: true,
      }),
    );
  };

  private emitCancel = (): void => {
    if (this.busy) return;
    this.dispatchEvent(new CustomEvent("lock-cancel", { bubbles: true, composed: true }));
  };
}

customElements.define("modulina-lock-panel", ModulinaLockPanel);
